import { type EditorLine, makeLine } from './line_rules';
import { mediaSelectionFromApi, type MediaSelection } from './StepMediaSelector';
import { parseStepTimestamps, type StepTimestamps } from './timestamp_tokens';
import type { ApiStep } from './types';

/**
 * A step editor's rich initial state when it can't come from a saved step:
 * edits carried across the create remount, or a duplicate of an existing
 * step. Rich (not the wire payload) so media keeps its thumbnails and lines
 * keep their rendered previews.
 */
export type StepSeed = {
   title: string;
   lines: EditorLine[];
   media: MediaSelection;
   timestampBaseline: StepTimestamps | null;
   timestampFields: { start: string; end: string };
};

export function linesFromStep(step: ApiStep | null): EditorLine[] {
   if (!step || step.lines.length === 0) {
      return [makeLine()];
   }
   return step.lines.map(line =>
      makeLine({
         text: line.text_raw,
         bullet: line.bullet,
         level: line.level,
         rendered: line.text_rendered,
      })
   );
}

/**
 * A duplicate's editor seed: the source step's content, opened as a
 * prefilled NEW step the author reviews and creates — never a silent POST.
 *
 * Media carries over by reference, which the API keeps safe per type:
 * imageids and objectids are step-level attachments, and editing media on
 * either step later (markers, crop, a different pick) swaps that step's
 * reference without touching the other. Video-guide timestamps are wikitext
 * tokens in the line text, so the copy starts on the source's segment and
 * the fields edit it independently.
 */
export function seedFromStep(step: ApiStep): StepSeed {
   const timestamps = parseStepTimestamps(step.lines.map(line => ({ text: line.text_raw })));
   return {
      title: step.title,
      lines: linesFromStep(step),
      media: mediaSelectionFromApi(step.media),
      timestampBaseline: timestamps,
      timestampFields: {
         start: timestamps === null ? '' : String(timestamps.start),
         end: timestamps === null ? '' : String(timestamps.end),
      },
   };
}
